import { Button, makeStyles, tokens } from '@fluentui/react-components';
import { useT } from '@powerportalspro/react';
import { useId, useState } from 'react';
import { MultiTableSearch, MultiTableSearchBox } from '@powerportalspro/react-fluent';
import { DemoShowcaseHeading, DemoShowcaseLayout } from './DemoShowcaseLayout';

// A search box as a site's header would have it, over the search page it sends the user to — here,
// this page, so the search below follows the box. The demo's accounts, contacts and opportunities,
// ten at a time, with views of a visitor's own once they sign in. Above them, a few searches that find
// something, each a click away: put in the page's address, as the box puts its search, for the search
// to follow.

const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Demos.MultiTableSearchShowcase.MultiTableSearchShowcaseMainDemo';

// Searches that find something in every table: names starting with "sch", one company's records, and
// open deals with a word inside their names. The tips say what each shows.
const SUGGESTIONS = ['sch', 'kemmer', '*metal'];

const useStyles = makeStyles({
  demo: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: tokens.spacingVerticalXL,
  },
  suggestions: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  list: {
    margin: 0,
    paddingInlineStart: tokens.spacingHorizontalXL,
  },
});

// The strings are HTML, written once for both stacks.
function Item({ text }: { text: string }) {
  return <li dangerouslySetInnerHTML={{ __html: text }} />;
}

export function MultiTableSearchShowcaseMainSample() {
  const styles = useStyles();
  const t = useT();
  const suggestionsId = useId();
  // Drawn again as a suggestion is picked, for the search to read the address it changed.
  const [, setPicks] = useState(0);

  // The search follows the page's address, so a suggestion goes there, as the search box's does.
  const searchFor = (term: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set('q', term);
    window.history.replaceState(window.history.state, '', url);
    setPicks((x) => x + 1);
  };

  return (
    <DemoShowcaseLayout
      title={t(`${STRINGS_BASE}.try-title`)}
      tips={
        <>
          <ul className={styles.list}>
            <Item text={t(`${STRINGS_BASE}.try-suggestions`)} />
            <Item text={t(`${STRINGS_BASE}.try-box`)} />
            <Item text={t(`${STRINGS_BASE}.try-shortcut`)} />
            <Item text={t(`${STRINGS_BASE}.try-see-all`)} />
            <Item text={t(`${STRINGS_BASE}.try-counts`)} />
            <Item text={t(`${STRINGS_BASE}.try-contains`)} />
            <Item text={t(`${STRINGS_BASE}.try-keys`)} />
            <Item text={t(`${STRINGS_BASE}.try-sort`)} />
            <Item text={t(`${STRINGS_BASE}.try-best-match`)} />
            <Item text={t(`${STRINGS_BASE}.try-filters`)} />
            <Item text={t(`${STRINGS_BASE}.try-layout`)} />
            <Item text={t(`${STRINGS_BASE}.try-recent`)} />
            <Item text={t(`${STRINGS_BASE}.try-saved-views`)} />
          </ul>

          <DemoShowcaseHeading>{t(`${STRINGS_BASE}.rules-title`)}</DemoShowcaseHeading>
          <ul className={styles.list}>
            <Item text={t(`${STRINGS_BASE}.rule-registered`)} />
            <Item text={t(`${STRINGS_BASE}.rule-security`)} />
            <Item text={t(`${STRINGS_BASE}.rule-address`)} />
          </ul>
        </>
      }
    >
      <div className={styles.demo}>
        <div className={styles.suggestions} role="group" aria-labelledby={suggestionsId}>
          <span id={suggestionsId}>{t(`${STRINGS_BASE}.suggestions-label`)}</span>
          {SUGGESTIONS.map((term) => (
            <Button key={term} size="small" appearance="outline" onClick={() => searchFor(term)}>
              {term}
            </Button>
          ))}
        </div>
        <MultiTableSearchBox searchPageUrl="demos/multi-table-search" style={{ width: 'min(480px, 100%)' }} />
        <MultiTableSearch pageSize={10} allowSavedViews savedViewsKey="multi-table-search-showcase" showTotals />
      </div>
    </DemoShowcaseLayout>
  );
}
